<script setup>
import clsx from 'clsx';
import router from '../util/router';
import { twMerge } from 'tailwind-merge';
import { ref, onMounted, onUnmounted } from 'vue';

const showProfileDropdown = ref(false);
const showAdminDropdown = ref(false);
const userRef = ref({
	name: "John Doe"
})

const isAdmin = ref(false);
const isPartner = ref(false);

const toggleProfileDropdown = () => {
	showProfileDropdown.value = !showProfileDropdown.value;
};

const toggleAdminDropdown = () => {
	showAdminDropdown.value = !showAdminDropdown.value;
}

const goToProfile = () => {
	router.push('/profile');
	showProfileDropdown.value = false;
};

const logout = () => {
	// Implement logout functionality here
	console.log('Logging out...');
	showProfileDropdown.value = false;
	showAdminDropdown.value = false;

	localStorage.removeItem('auth_token');
	localStorage.removeItem('user');
	localStorage.removeItem('expiresAt');

	router.replace('/login');
	// You might want to redirect to login page or call a logout API
};

// Close dropdown when clicking outside
const closeDropdownOnOutsideClick = (event) => {
	if (showProfileDropdown.value && !event.target.closest('.relative')) {
		showProfileDropdown.value = false;
	}

	if (showAdminDropdown.value && !event.target.closest('.relative')) {
		showAdminDropdown.value = false;
	}
};

const goToSettings = () => {
	router.push('/admin/settings');
	showAdminDropdown.value = false;
};

const goToUsers = () => {
	router.push('/admin/users');
	showAdminDropdown.value = false;
};

const goToContainerTypes = () => {
	router.push('/admin/container-types');
	showAdminDropdown.value = false;
};

const goToServiceTypes = () => {
	router.push('/admin/service-types');
	showAdminDropdown.value = false;
};

const goToPortsAgents = () => {
	router.push('/admin/ports-agents');
	showAdminDropdown.value = false;
};

const goToProNumberConfig = () => {
	router.push('/admin/config');
	showAdminDropdown.value = false;
};

console.log(userRef.value);

// Add event listener when component is mounted
onMounted(() => {

	const user = localStorage.getItem('user');
	const userDetails = JSON.parse(user);

	if (!userDetails) {
		return;
	}

	userRef.value = {
		...userDetails,
		initials: userDetails.name ? userDetails.name.split(' ').map(part => part[0]).join('') : '',
	};

	isAdmin.value = userDetails['role'] === "ADMIN" ?? false;
	isPartner.value = userDetails['role'] === "PARTNER" ?? false;

	console.log(userDetails);

	document.addEventListener('click', closeDropdownOnOutsideClick);
});

// Remove event listener when component is unmounted
onUnmounted(() => {
	document.removeEventListener('click', closeDropdownOnOutsideClick);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>

<template>
	<div
		class="min-w-[4.5rem] w-[4.5rem] max-w-full h-full bg-white drop-shadow-[2px_2px_6px_rgba(187,195,206,0.4)] rounded-[1.25rem] flex flex-col justify-between items-center">
		<button v-on:click="() => router.push('/')"
			class="h-[100px] w-full flex items-center justify-center border-b-2 border-black cursor-pointer">
			<img src="/logo/logo.svg" />
		</button>
		<div class="h-full flex flex-col items-center w-full gap-y-10 *:flex *:justify-center *:items-center">
			<div></div>
			<div class="w-full">
				<button v-on:click="() => router.push('/')" role="button"
					class="group transition-all duration-[750ms] ease-in-out flex flex-col gap-2 justify-center items-center">
					<svg width="21" height="20" viewBox="0 0 21 20" :class="twMerge('group-hover:fill-secondary transition-all duration-[750ms] ease-in-out', clsx({
						'fill-secondary': ($route.fullPath === '/'),
					}))" class="" fill="none">
						<path
							d="M8.21259 1.15016L8.21298 1.14985C9.29722 0.279042 11.0374 0.284136 12.1379 1.16073C12.138 1.16084 12.1382 1.16094 12.1383 1.16105L18.684 6.39756C18.6845 6.39802 18.6851 6.39849 18.6857 6.39895C19.0629 6.70711 19.3896 7.18942 19.6003 7.74099C19.8109 8.29218 19.8895 8.86987 19.8162 9.35096L18.5573 16.8845C18.5572 16.885 18.5571 16.8855 18.557 16.8859C18.3074 18.3188 16.9131 19.5 15.4699 19.5H4.86988C3.40541 19.5 2.04242 18.3476 1.79286 16.8965C1.7928 16.8961 1.79274 16.8958 1.79268 16.8955L0.533048 9.35761L0.532854 9.35646C0.450708 8.8718 0.524437 8.29296 0.734569 7.74192C0.94468 7.19094 1.27541 6.70909 1.66173 6.40086L1.6626 6.40016L8.21259 1.15016ZM10.1699 17.25C10.856 17.25 11.4199 16.6862 11.4199 16V13C11.4199 12.3139 10.856 11.75 10.1699 11.75C9.48374 11.75 8.91988 12.3139 8.91988 13V16C8.91988 16.6862 9.48374 17.25 10.1699 17.25Z"
							stroke="#020754" />
					</svg>
					<p class="font-rope font-semibold text-[70%] text-secondary">HOME</p>
				</button>
			</div>
			<div class="w-full">
				<button v-on:click="() => router.push('/military-shipment')"
					class="group transition-all duration-[750ms] ease-in-out flex flex-col gap-2 justify-center items-center"
					role="button">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" :class="twMerge('group-hover:fill-secondary transition-all duration-[750ms] ease-in-out', clsx({
						'fill-secondary': ($route.fullPath.indexOf('/military-shipment') !== -1),
					}))">
						<path
							d="M21.1416 14.5527L20.7319 16.4117C20.7318 16.4119 20.7318 16.412 20.7318 16.4121C20.0693 19.397 17.7515 21.5 14.38 21.5H9.61998C6.2485 21.5 3.93063 19.397 3.2682 16.4121C3.26817 16.412 3.26814 16.4119 3.26811 16.4117L2.85833 14.5527C2.85832 14.5527 2.85831 14.5526 2.85829 14.5526C2.71065 13.8804 3.12395 13.0925 3.7666 12.8339L5.18611 12.2641L5.18624 12.2641L10.694 10.055C10.6943 10.0548 10.6947 10.0547 10.695 10.0546C11.1187 9.88697 11.5661 9.80005 12 9.80005C12.4339 9.80005 12.8812 9.88697 13.305 10.0546C13.3053 10.0547 13.3056 10.0548 13.306 10.055L18.8137 12.2641L18.8139 12.2641L20.2334 12.8339C20.8761 13.0925 21.2894 13.8805 21.1416 14.5527Z"
							stroke="#020754" />
						<path
							d="M18.5 8V9.43749L18.4959 9.43584L14.2259 7.72584L14.2259 7.72581L14.2214 7.72406C12.7942 7.16845 11.204 7.1686 9.76674 7.7348L9.76673 7.73479L9.76374 7.73599L5.50374 9.44599L5.50371 9.4459L5.5 9.44747V8C5.5 6.62614 6.62614 5.5 8 5.5H16C17.3739 5.5 18.5 6.62614 18.5 8Z"
							stroke="#020754" />
						<path d="M14 4.5H10V3C10 2.72614 10.2261 2.5 10.5 2.5H13.5C13.7739 2.5 14 2.72614 14 3V4.5Z"
							stroke="#020754" />
					</svg>
					<p class="font-rope font-semibold text-[70%] text-secondary">MILITARY</p>
				</button>
			</div>
			<div class="w-full">
				<button v-on:click="() => router.push('/consumer-shipment')" role="button"
					class="group transition-all duration-[750ms] ease-in-out flex flex-col gap-2 justify-center items-center">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" :class="twMerge('group-hover:fill-secondary transition-all duration-[750ms] ease-in-out', clsx({
						'fill-secondary': ($route.fullPath.indexOf('/consumer-shipment') !== -1),
					}))">
						<path
							d="M11.7374 11.8455L4.04142 7.38777C4.04129 7.3877 4.04117 7.38762 4.04105 7.38755C3.74671 7.216 3.69765 6.83149 3.8928 6.61311L3.89555 6.61C4.15149 6.31876 4.43633 6.08799 4.72852 5.92937L4.72852 5.92938L4.73211 5.9274L10.1521 2.9274L10.1521 2.92741L10.1544 2.92613C10.6417 2.65306 11.3122 2.50244 12.01 2.50244C12.7077 2.50244 13.3782 2.65306 13.8656 2.92614L13.8678 2.9274L19.2878 5.9274L19.2878 5.92741L19.2914 5.92937C19.5771 6.08445 19.8619 6.3216 20.1234 6.60898C20.3055 6.82971 20.2517 7.21691 19.9589 7.38756C19.9588 7.38763 19.9586 7.3877 19.9585 7.38777L12.2594 11.8473L12.2589 11.8475C12.1072 11.9356 11.9072 11.9399 11.7374 11.8455Z"
							stroke="#020754" />
						<path
							d="M4.69898 18.5563L4.69278 18.5529C4.18159 18.2627 3.68577 17.7315 3.31593 17.0883C2.94564 16.4443 2.72998 15.7372 2.72998 15.13V9.97C2.72998 9.54577 3.16003 9.31462 3.49036 9.50323C3.49065 9.5034 3.49093 9.50356 3.49122 9.50373L10.6694 13.6669C10.8277 13.7704 10.93 13.9522 10.93 14.14V20.96C10.93 21.3667 10.5325 21.6017 10.199 21.4405C9.17718 20.9395 7.80153 20.2182 6.67739 19.6199C6.11595 19.3211 5.61871 19.0537 5.26185 18.8611C5.08344 18.7648 4.94015 18.6872 4.84152 18.6337L4.72826 18.5722L4.69898 18.5563Z"
							stroke="#020754" />
						<path
							d="M13.0699 20.96V14.14C13.0699 13.9522 13.1722 13.7704 13.3306 13.6669L20.5087 9.50373C20.509 9.50356 20.5093 9.50339 20.5096 9.50322C20.8399 9.31463 21.2699 9.54578 21.2699 9.97V15.13C21.2699 15.7372 21.0543 16.4443 20.684 17.0883C20.3142 17.7315 19.8183 18.2627 19.3071 18.5529L19.3009 18.5563L19.2717 18.5722L19.1584 18.6337C19.0598 18.6872 18.9165 18.7648 18.7381 18.8611C18.3812 19.0537 17.884 19.3211 17.3225 19.6199C16.1984 20.2182 14.8228 20.9395 13.8009 21.4405C13.4675 21.6017 13.0699 21.3667 13.0699 20.96Z"
							stroke="#020754" />
					</svg>
					<p class="font-rope font-semibold text-[70%] text-secondary">CONSUMER</p>
				</button>
			</div>
			<div class="w-full" v-if="isPartner">
				<button v-on:click="() => router.push('/partners/users')" role="button"
					class="group transition-all duration-[750ms] ease-in-out flex flex-col gap-2 justify-center items-center">
					<svg :class="twMerge('group-hover:fill-secondary transition-all duration-[750ms] ease-in-out', clsx({
						'fill-secondary': ($route.fullPath === '/partners/users'),
					}))" width="21" height="20" viewBox="0 0 75 75" fill="none">
						<path
							d="M22.4745 42.8151L22.4742 42.8155C21.4095 44.2431 21.4316 45.9339 22.2037 47.2551C22.9728 48.571 24.4728 49.5056 26.3333 49.5056H48.6667C50.5081 49.5056 52.0065 48.5697 52.7798 47.2565C53.5567 45.9374 53.5923 44.2455 52.5258 42.8155L52.5255 42.8151C49.5772 38.8715 43.9116 36.273 37.5 36.273C31.0884 36.273 25.4228 38.8715 22.4745 42.8151ZM12.5 0.5H62.5C69.1956 0.5 74.5 5.31883 74.5 11.1445V52.0826C74.5 57.9082 69.1956 62.727 62.5 62.727H59.375C55.9704 62.727 52.6402 63.9291 50.2077 66.1194C50.2075 66.1196 50.2073 66.1198 50.2071 66.12L43.0845 72.4593C43.0844 72.4594 43.0843 72.4595 43.0842 72.4595C40.0237 75.1802 35.0179 75.1802 31.9574 72.4595C31.9573 72.4595 31.9573 72.4594 31.9572 72.4593L24.8346 66.12C24.8343 66.1198 24.8341 66.1195 24.8338 66.1193C22.4019 63.9296 19.1137 62.727 15.6667 62.727H12.5C5.80436 62.727 0.5 57.9082 0.5 52.0826V11.1445C0.5 5.31883 5.80436 0.5 12.5 0.5ZM47.7083 22.8143C47.7083 17.6496 43.1006 13.5713 37.5 13.5713C31.8994 13.5713 27.2917 17.6496 27.2917 22.8143C27.2917 27.9401 31.8979 32.0572 37.5 32.0572C43.1006 32.0572 47.7083 27.9789 47.7083 22.8143Z"
							stroke="#020754" stroke-width="4" />
					</svg>
					<p class="font-rope font-semibold text-[70%] text-secondary">PARTNERS</p>
				</button>
			</div>
			<div class="w-full">
				<button v-on:click="() => router.push('/me/contact-us')" role="button" class="group transition-all duration-[750ms] ease-in-out flex flex-col gap-2 justify-center items-center">
					<svg xmlns="http://www.w3.org/2000/svg" :class="twMerge('stroke-secondary group-hover:stroke-primary transition-all duration-[750ms] ease-in-out', clsx({
						'stroke-primary': ($route.fullPath === '/contact-us'),
					}))" width="26" height="25" fill="none" viewBox="0 0 24 24" >
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
							d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
					</svg>
					<p class="font-rope font-semibold text-[70%] text-secondary">CONTACT US</p>
				</button>
			</div>
			<div class="w-full" v-if="isAdmin">
				<button role="button" @click="toggleAdminDropdown"
					class="transition-all duration-[750ms] ease-in-out relative group flex flex-col gap-2 justify-center items-center">
					<svg :class="twMerge('group-hover:fill-secondary transition-all duration-[750ms] ease-in-out', clsx({
						'fill-secondary': ($route.fullPath === '/admin'),
					}))" width="21" height="20" viewBox="0 0 75 75" fill="none">
						<path
							d="M22.4745 42.8151L22.4742 42.8155C21.4095 44.2431 21.4316 45.9339 22.2037 47.2551C22.9728 48.571 24.4728 49.5056 26.3333 49.5056H48.6667C50.5081 49.5056 52.0065 48.5697 52.7798 47.2565C53.5567 45.9374 53.5923 44.2455 52.5258 42.8155L52.5255 42.8151C49.5772 38.8715 43.9116 36.273 37.5 36.273C31.0884 36.273 25.4228 38.8715 22.4745 42.8151ZM12.5 0.5H62.5C69.1956 0.5 74.5 5.31883 74.5 11.1445V52.0826C74.5 57.9082 69.1956 62.727 62.5 62.727H59.375C55.9704 62.727 52.6402 63.9291 50.2077 66.1194C50.2075 66.1196 50.2073 66.1198 50.2071 66.12L43.0845 72.4593C43.0844 72.4594 43.0843 72.4595 43.0842 72.4595C40.0237 75.1802 35.0179 75.1802 31.9574 72.4595C31.9573 72.4595 31.9573 72.4594 31.9572 72.4593L24.8346 66.12C24.8343 66.1198 24.8341 66.1195 24.8338 66.1193C22.4019 63.9296 19.1137 62.727 15.6667 62.727H12.5C5.80436 62.727 0.5 57.9082 0.5 52.0826V11.1445C0.5 5.31883 5.80436 0.5 12.5 0.5ZM47.7083 22.8143C47.7083 17.6496 43.1006 13.5713 37.5 13.5713C31.8994 13.5713 27.2917 17.6496 27.2917 22.8143C27.2917 27.9401 31.8979 32.0572 37.5 32.0572C43.1006 32.0572 47.7083 27.9789 47.7083 22.8143Z"
							stroke="#020754" stroke-width="4" />
					</svg>

					<transition name="fade">
						<div v-if="showAdminDropdown"
							class="absolute left-[2.7rem] top-0 w-48 bg-white rounded-lg shadow-lg py-2 !z-[999] *:!z-[999] before:content-[''] before:absolute before:top-[1rem] before:-left-[1.0rem] before:border-8 before:border-transparent before:border-r-white">
							<div @click="goToContainerTypes" class="px-4 py-2 hover:bg-gray-100 cursor-pointer">
								Container Types</div>
							<div @click="goToServiceTypes" class="px-4 py-2 hover:bg-gray-100 cursor-pointer">Service
								Types</div>
							<div @click="goToPortsAgents" class="px-4 py-2 hover:bg-gray-100 cursor-pointer">Ports &
								Agents</div>
							<div @click="goToUsers" class="px-4 py-2 hover:bg-gray-100 cursor-pointer">Users & Access
							</div>
							<div @click="goToProNumberConfig" class="px-4 py-2 hover:bg-gray-100 cursor-pointer">Pro
								Number Config
							</div>
						</div>
					</transition>
					<p class="font-rope font-semibold text-[70%] text-secondary">ADMIN</p>
				</button>
			</div>
		</div>
		<div class="h-[120px] flex justify-center items-center cursor-pointer relative">
			<div @click="toggleProfileDropdown" class="relative flex flex-col gap-2 justify-center items-center">
				<img :src='`https://api.dicebear.com/9.x/initials/svg?seed=${userRef?.initials ?? userRef?.name}&radius=50&fontFamily=sans-serif,Arial&fontWeight=500&scale=80`'
					class="w-10 h-10" />
				<transition name="fade">
					<div v-if="showProfileDropdown"
						class="absolute left-[2.7rem] -bottom-4 w-36 bg-white rounded-lg shadow-lg py-2 !z-[999] *:!z-[999] before:content-[''] before:absolute before:top-[55%] before:-left-[1.0rem] before:-translate-y-1/2 before:border-8 before:border-transparent before:border-r-white">
						<div @click="goToProfile" class="px-4 py-2 hover:bg-gray-100 cursor-pointer">Profile</div>
						<div @click="logout" class="px-4 py-2 hover:bg-gray-100 cursor-pointer">Logout</div>
					</div>
				</transition>
				<button @click="logout" class="font-rope font-semibold text-[70%] text-secondary">LOGOUT</button>
			</div>
		</div>
	</div>
</template>
